import { FlattenedSchemaItem, SchemaItem, StructureColumnTypeEnum, StructureItemType } from "./Models/Models";

export function filterHierarchy(
    schemaItems: SchemaItem[],
    include: StructureColumnTypeEnum[]
): SchemaItem[] {
    return schemaItems.map(item => {
        if (!item.refSchema?.schemaItems) {
            return item;
        }

        return {
            ...item,
            refSchema: {
                ...item.refSchema,
                schemaItems: filterHierarchy(
                    item.refSchema.schemaItems.filter(s => include.includes(s.typeId)),
                    include
                )
            }
        };
    });
}

export function getSchemaNamesDictionary(
    schemaItems: SchemaItem[],
    prefix?: string
): FlattenedSchemaItem[] {
    const result: FlattenedSchemaItem[] = [];

    for (const item of schemaItems) {
        if (item.refSchema?.schemaItems) {
            const childPrefix = prefix ? `${prefix}.${item.name}` : item.name;
            const children = getSchemaNamesDictionary(item.refSchema.schemaItems, childPrefix);
            result.push(...children);
        } else {
            const key = prefix ? `${prefix}.${item.name}` : item.name;
            result.push({ key, schemaItem: item });
        }
    }

    return result;
}

export function getDefaultValueForSchemaItem(item: SchemaItem): unknown {
    switch (item.valueTypeId) {
        case StructureItemType.Category: {
            const defaultCategory =
                item.categories && item.categories.length > 0
                    ? item.categories[0].value
                    : 'default';
            return defaultCategory;
        }
        case StructureItemType.String:
            return "example";
        case StructureItemType.Number:
            return 0;
        case StructureItemType.NumberArray:
            return new Array(item.arrayLength ?? 0).fill(0);
        default:
            return 0;
    }
}

export type SchemaItemValueParseResult = {
    value?: unknown;
    error?: string;
}

export function isOptionalSchemaItem(item: SchemaItem): boolean {
    return item.typeId === StructureColumnTypeEnum.Info;
}

export function parseSchemaItemValue(item: SchemaItem, rawValue: string): SchemaItemValueParseResult {
    const trimmed = rawValue.trim();
    if (!trimmed) {
        return { error: "Value is required" };
    }

    switch (item.valueTypeId) {
        case StructureItemType.Number: {
            const value = Number(trimmed);
            return isNaN(value) ? { error: "Value should be a number" } : { value };
        }
        case StructureItemType.Category: {
            const category = item.categories?.find(x => x.value === trimmed);
            return category ? { value: category.value } : { error: "Value should be one of the categories" };
        }
        case StructureItemType.NumberArray: {
            const parts = trimmed.split(",").map(x => x.trim());
            if (parts.some(x => x === "" || isNaN(Number(x)))) {
                return { error: "Value should be a comma separated list of numbers" };
            }
            if (item.arrayLength && parts.length !== item.arrayLength) {
                return { error: `Value should contain ${item.arrayLength} numbers` };
            }
            return { value: parts.map(Number) };
        }
        default:
            return { value: trimmed };
    }
}
